import React, { useState, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import StatusToggle from "./StatusToggle";
import ChangePasswordModal from "./ChangePasswordModal";
import ConfirmDialog from "./common/ConfirmDialog";
import { UilBars } from "@iconscout/react-unicons";
import api from "../utils/api";
import { reconnectSocket } from "../socket";
import { stopPushForThisDevice } from "../utils/pushNotifications";

const Layout = ({ children, setIsAuthenticated, hideHeader = false }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [restaurantStatus, setRestaurantStatus] = useState("online");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [username, setUsername] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [amcEndDate, setAmcEndDate] = useState("");
  const userMenuRef = useRef(null);

  const parseYmdAsLocalDate = (ymd) => {
    if (!ymd || typeof ymd !== "string") return null;
    // Interpret "YYYY-MM-DD" as local midnight (avoid UTC shifting).
    const d = new Date(`${ymd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const startOfToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    stopPushForThisDevice(); // no more order alerts on this device
    ["token", "username", "role", "userId", "mustChangePassword", "rememberMe", "savedUsername"]
      .forEach((k) => localStorage.removeItem(k));
    reconnectSocket(); // drop this screen's staff access to live updates
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Note: We don't auto-change sidebarCollapsed on resize to preserve user interactions
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Fetch initial restaurant status
    api
      .get("/settings/restaurantstatus")
      .then((res) => setRestaurantStatus(res.data.status))
      .catch((err) => console.error("Error fetching status:", err));
  }, []);

  useEffect(() => {
    const loadAmc = async () => {
      try {
        const res = await api.get("/settings/amc");
        if (res.data?.success && res.data?.amc?.endDate) {
          setAmcEndDate(res.data.amc.endDate || "");
        } else {
          setAmcEndDate("");
        }
      } catch {
        // AMC is optional
        setAmcEndDate("");
      }
    };
    loadAmc();
  }, []);

  useEffect(() => {
    // Get username from localStorage
    const storedUsername = localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  const amcEnd = parseYmdAsLocalDate(amcEndDate);
  const today = startOfToday();
  const daysLeft =
    amcEnd
      ? Math.ceil((amcEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
      : null;
  const showAmcExpiryWarning =
    typeof daysLeft === "number" && daysLeft >= 0 && daysLeft <= 30;
  const formattedAmcEnd = amcEnd
    ? new Intl.DateTimeFormat("en-IN", { year: "numeric", month: "short", day: "2-digit" }).format(amcEnd)
    : "";

  return (
    <div className="flex h-screen w-full bg-white overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {!sidebarCollapsed && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        flex-shrink-0 bg-white transition-all duration-300 ease-in-out
        border-r border-gray-200 shadow-sm z-50
        fixed inset-y-0 left-0
        ${isMobile ? (sidebarCollapsed ? "hidden" : "w-56") : sidebarCollapsed ? "w-16" : "w-56"}
        md:relative
      `}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 h-screen overflow-hidden">
        {/* Header - Conditionally rendered */}
        {!hideHeader && (
          <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm relative z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Sidebar Toggle Button - Desktop Only */}
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className={`
                    hidden md:flex items-center justify-center transition-all duration-200 
                    text-gray-600 hover:text-gray-800 shadow-sm border border-gray-200
                    ${sidebarCollapsed
                      ? "w-12 h-12 bg-green-100 hover:bg-green-200 active:bg-green-300 text-green-700"
                      : "w-12 h-12 bg-blue-100 hover:bg-blue-200 active:bg-blue-300 text-blue-700"
                    }
                    hover:scale-105 active:scale-95 rounded-lg

                  `}
                  title={
                    sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"
                  }
                >
                  <UilBars size={24} />
                </button>
                <h1 className="font-display font-semibold text-xl text-gray-900">
                  Kairalimenu Admin
                </h1>
                <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
                  <span
                    className={`w-2 h-2 rounded-full ${restaurantStatus === "online" ? "bg-green-500" : "bg-red-500"}`}
                  ></span>
                  {restaurantStatus === "online"
                    ? "Restaurant Online"
                    : "Restaurant Offline"}
                </div>
                {showAmcExpiryWarning && (
                  <div className="hidden lg:flex items-center">
                    <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1 text-xs font-semibold">
                      {daysLeft === 0
                        ? `Plan expires today (${formattedAmcEnd})`
                        : `Plan expiring in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${formattedAmcEnd})`}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <StatusToggle
                  status={restaurantStatus}
                  setStatus={setRestaurantStatus}
                />
                {/* User Avatar Dropdown */}
                {username && (
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 hover:border-gray-400 transition-all duration-200 group"
                    >
                      <div className="w-7 h-7 rounded-full bg-black text-white text-xs font-bold flex items-center justify-center uppercase">
                        {username.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-black">
                        {username}
                      </span>
                      <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${showUserMenu ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-xs text-gray-400 uppercase tracking-widest">Signed in as</p>
                          <p className="text-sm font-semibold text-black truncate mt-0.5">{username}</p>
                        </div>
                        <button
                          onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                          Change Password
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {showAmcExpiryWarning && (
              <div className="mt-3 lg:hidden">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  {daysLeft === 0
                    ? `Plan expires today (${formattedAmcEnd})`
                    : `Plan expiring in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${formattedAmcEnd})`}
                </div>
              </div>
            )}
          </header>
        )}

        {/* Mobile Topbar - Show minimal version when header is hidden */}
        {hideHeader && (
          <div
            className="bg-white px-5 py-3.5 border-b border-gray-200 z-[40] relative"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
              {/* Desktop Sidebar Toggle - Show when header is hidden */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`
                  hidden md:flex items-center justify-center transition-all duration-200 
                  text-gray-600 hover:text-gray-800 shadow-sm border border-gray-200
                  ${sidebarCollapsed
                    ? "w-10 h-10 bg-green-100 hover:bg-green-200 active:bg-green-300 text-green-700"
                    : "w-10 h-10 bg-blue-100 hover:bg-blue-200 active:bg-blue-300 text-blue-700"
                  }
                  hover:scale-105 active:scale-95 rounded-lg
                `}
                title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              >
                <UilBars size={20} />
              </button>
              {/* Mobile Sidebar Toggle */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg
                         bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors duration-200
                         text-gray-600 hover:text-gray-800 shadow-sm border border-gray-200"
                title={sidebarCollapsed ? "Open Sidebar" : "Close Sidebar"}
              >
                <UilBars size={20} />
              </button>
              {showAmcExpiryWarning && (
                <div className="hidden sm:flex items-center">
                  <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1 text-xs font-semibold">
                    {daysLeft === 0
                        ? `Plan expires today (${formattedAmcEnd})`
                        : `Plan expiring in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${formattedAmcEnd})`}
                  </span>
                </div>
              )}
              </div>
              {/* Right side - User Avatar Dropdown */}
              {username && (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 hover:border-gray-400 transition-all duration-200"
                  >
                    <div className="w-7 h-7 rounded-full bg-black text-white text-xs font-bold flex items-center justify-center uppercase">
                      {username.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden sm:block">{username}</span>
                    <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${showUserMenu ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-xs text-gray-400 uppercase tracking-widest">Signed in as</p>
                      <p className="text-sm font-semibold text-black truncate mt-0.5">{username}</p>
                    </div>
                    <button
                      onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      Change Password
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
                </div>
              )}
            </div>

            {showAmcExpiryWarning && (
              <div className="mt-2 sm:hidden">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  {daysLeft === 0
                    ? `Plan expires today (${formattedAmcEnd})`
                    : `Plan expiring in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${formattedAmcEnd})`}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
          <div
            className={`${hideHeader ? "h-full px-4 py-4" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"}`}
          >
            {children ?? <Outlet />}
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      {/* Logout Confirmation */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
        title="Logout Confirmation"
        message="Are you sure you want to logout? You will need to sign in again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      />
    </div>
  );
};

export default Layout;
